
import { createContext, useCallback, useContext, useEffect, useState } from "react";

// Minimal light/dark theme provider (replaces next-themes, whose injected
// inline <script> trips a React 19 console error). The theme persists in a
// cookie so the server layout renders the right <html> class on first paint,
// no anti-flash script needed.

type Theme = "light" | "dark";

const ThemeContext = createContext<{
  resolvedTheme: Theme;
  setTheme: (t: Theme) => void;
}>({ resolvedTheme: "light", setTheme: () => {} });

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [resolvedTheme, setResolvedTheme] = useState<Theme>("light");

  // Sync state with the class the server layout rendered from the cookie. Deferred
  // out of the effect body to satisfy react-hooks/set-state-in-effect.
  useEffect(() => {
    const id = setTimeout(() => {
      setResolvedTheme(
        document.documentElement.classList.contains("dark") ? "dark" : "light",
      );
    }, 0);
    return () => clearTimeout(id);
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setResolvedTheme(t);
    document.documentElement.classList.toggle("dark", t === "dark");
    document.cookie = `theme=${t}; path=/; max-age=31536000; samesite=lax`;
  }, []);

  return (
    <ThemeContext.Provider value={{ resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
